import { singleton, onCleanup } from './cleanup.js'

// Lista de todas las clases posibles del cursor para reseteo
const cursorClasses = [
  'exploramas',
  'consultas',
  'tailormade',
  'readymade',
  'proyecto',
  'customizar',
  'arrow-left',
  'arrow-right',
  'scroller'
]

// Mapa de selector → clase que aplica el cursor al hacer hover
const hoverMap = [
  ['.cursor-exploramas', 'exploramas'],
  ['.cursor-tailormade', 'tailormade'],
  ['.cursor-readymade', 'readymade'],
  ['.cursor-consultas', 'consultas'],
  ['.swiper-button-prev', 'arrow-left'],
  ['.swiper-button-next', 'arrow-right'],
  ['.cursor-proyecto', 'proyecto'],
  ['.cursor-customizar', 'customizar'],
  ['.cursor-scroller', 'scroller']
]

/**
 * @returns {HTMLElement|null} El nodo #cursor (vive fuera del transition-wrapper)
 */
function getCursorEl () {
  return document.querySelector('#cursor')
}

/** Remueve todas las clases de estado del cursor. */
function resetCursor () {
  const cursor = getCursorEl()
  if (!cursor) return

  cursorClasses.forEach(className => cursor.classList.remove(className))
}

/**
 * Instala el seguimiento del puntero: listener de mousemove, bucle de
 * interpolación y observador del estado de transición.
 *
 * Todo esto se engancha a `document` / `document.body` / `#cursor`, que
 * sobreviven a las navegaciones. Por eso se instala UNA sola vez: antes se
 * reinstalaba en cada transición, acumulando un listener de mousemove, un
 * bucle rAF y un MutationObserver por navegación.
 */
function installCursorTracking () {
  let mouseX = 0
  let mouseY = 0
  let cursorX = 0
  let cursorY = 0
  const delay = 0.1 // Factor de suavizado (0.1 = más delay, 0.9 = menos delay)

  document.addEventListener('mousemove', (e) => {
    mouseX = e.clientX
    mouseY = e.clientY
  }, { passive: true })

  const animateCursor = () => {
    const cursor = getCursorEl()

    if (cursor) {
      // Interpolación lineal para crear el efecto de delay
      cursorX += (mouseX - cursorX) * delay
      cursorY += (mouseY - cursorY) * delay

      cursor.style.transform = `translate(${cursorX}px, ${cursorY}px)`
    }

    requestAnimationFrame(animateCursor)
  }

  requestAnimationFrame(animateCursor)

  // Resetear cursor al inicio de una transición (cuando el body tiene la clase page-transitioning)
  const observer = new MutationObserver(() => {
    if (document.body.classList.contains('page-transitioning')) {
      resetCursor()
    }
  })

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  })

  return observer
}

/**
 * Verifica si el elemento o alguno de sus padres tiene la clase cursor-avoid
 * @param {Element|null} element
 * @returns {boolean}
 */
function hasCursorAvoidClass (element) {
  if (!element || element === document.body) return false
  if (element.classList && element.classList.contains('cursor-avoid')) return true
  return hasCursorAvoidClass(element.parentElement)
}

/**
 * Vincula el efecto de hover sobre un grupo de elementos y registra su limpieza.
 * @param {NodeListOf<Element>} elements
 * @param {string} className Clase de cursor a aplicar
 */
function handleHover (elements, className) {
  const cursor = getCursorEl()
  if (!cursor) return

  elements.forEach(element => {
    const onMouseOver = (e) => {
      // Si el elemento sobre el que está el cursor tiene la clase cursor-avoid o está dentro de uno, no aplicar el efecto
      if (hasCursorAvoidClass(e.target)) {
        cursor.classList.remove(className)
        return
      }
      // Para la clase 'scroller', excluir inputs y sus labels/contenedores
      if (className === 'scroller' && (e.target.tagName === 'INPUT' || e.target.closest('label') || e.target.closest('.checkbox'))) {
        cursor.classList.remove(className)
        return
      }
      cursor.classList.add(className)
    }

    const onMouseOut = (e) => {
      // Si el elemento al que va el cursor está dentro del mismo contenedor y no tiene la clase cursor-avoid
      if (element.contains(e.relatedTarget) && !hasCursorAvoidClass(e.relatedTarget)) {
        return
      }
      cursor.classList.remove(className)
    }

    element.addEventListener('mouseover', onMouseOver)
    element.addEventListener('mouseout', onMouseOut)

    onCleanup(() => {
      element.removeEventListener('mouseover', onMouseOver)
      element.removeEventListener('mouseout', onMouseOut)
    })
  })
}

/**
 * Inicializa el cursor personalizado.
 * El seguimiento del puntero es singleton; los hovers, que apuntan a nodos
 * dentro del transition-wrapper, se revinculan en cada navegación.
 */
function cursor () {
  singleton('cursor:tracking', installCursorTracking)

  resetCursor()

  hoverMap.forEach(([selector, className]) => {
    const elements = document.querySelectorAll(selector)
    if (elements.length) handleHover(elements, className)
  })
}

export default cursor
